import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  TreePine, Check, ArrowRight, Building2, Globe, Leaf,
  Users, Shield, MapPin
} from 'lucide-react';

const PublicPage = () => {
  return (
    <div data-testid="public-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a5f3c 0%, #0d3d25 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <TreePine className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Infrastrukturë Publike</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Mobilitet i Qëndrueshëm për Qytetet
            </h1>
            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Mbështetni tranzicionin drejt transportit të pastër duke instaluar infrastrukturë karikimi 
              në hapësira publike. Ideal për bashki, institucione dhe organizata.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/contact">
                  Na Kontaktoni
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
              Ndërtojmë Infrastrukturën e së Ardhmes
            </h2>
            <p className="text-lg text-slate-500 leading-relaxed">
              AlbMove bashkëpunon me bashkitë dhe institucionet publike për të krijuar një rrjet të gjerë 
              stacionesh karikimi në hapësira publike. Qëllimi ynë është të bëjmë transportin elektrik 
              të aksesueshëm për të gjithë qytetarët.
            </p>
          </div>
        </div>
      </section>
      
      {/* Solutions */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Zgjidhje për Sektorin Publik
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                icon: Building2, 
                title: 'Bashki & Komuna', 
                desc: 'Instalim karikuesish në parkingje publike, sheshe dhe zona qendrore',
                features: ['Monitorim qendror', 'Raporte për qytetarët', 'Tarifim fleksibël']
              },
              { 
                icon: MapPin, 
                title: 'Infrastrukturë Rrugore', 
                desc: 'Stacione karikimi përgjatë akseve kryesore dhe autostradave',
                features: ['Fast Charging DC', 'Disponueshmëri 24/7', 'Sinjalistikë e dedikuar']
              },
              { 
                icon: Users, 
                title: 'Institucione Publike', 
                desc: 'Karikues për flotën e institucioneve dhe publikun',
                features: ['Flota qeveritare', 'Akses publik', 'Faturim i centralizuar']
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 border border-slate-200/50">
                <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] mb-3">{item.title}</h3>
                <p className="text-slate-500 mb-4">{item.desc}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-lime-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Impact */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Impakti në Komunitet
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Leaf, title: 'Reduktim i Emisioneve', desc: 'Ndihmoni në uljen e ndotjes së ajrit në qytete' },
                  { icon: Globe, title: 'Qytet Modern', desc: 'Pozicionohuni si qytet inovativ dhe i qëndrueshëm' },
                  { icon: Users, title: 'Shërbim për Qytetarët', desc: 'Ofroni infrastrukturë moderne për banorët' },
                  { icon: Shield, title: 'Siguri Energjetike', desc: 'Reduktoni varësinë nga karburantet e importuara' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1767122230347-03e29dbf06ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwdWJsaWMlMjBwYXJrJTIwY2l0eSUyMGdyZWVuJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzcwOTQ3MjA1fDA&ixlib=rb-4.1.0&q=85"
                alt="Park publik i gjelbër"
                className="rounded-3xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1a5f3c 0%, #0d3d25 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Partner për Qytete të Qëndrueshme
            </h2>
            <p className="text-green-100 mb-8 max-w-xl mx-auto">
              Kontaktoni ekipin tonë për të diskutuar partneritete dhe projekte infrastrukturore.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">
                Diskutoni Projektin
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicPage;
